import { useEffect, useState, type ReactNode } from 'react';
import { Button, Flash } from '@primer/react';

import {
  parseTeamDocument,
  ensureEpicCoordinatorWorkspace,
  readEpicCoordinatorSource,
  readTeam,
  serializeTeamDocument,
  writeEpicCoordinatorSource,
} from '../../logic/team-editor.js';
import { useApplyTeamDocument, describeSaveFailure } from '../apply-team.js';
import { QueryPhase, Section } from '../components/state.js';
import { useTeamVersionDocument, useTeamVersions } from '../queries.js';
import { useStore } from '../store-context.js';

const STARTER = `epicCoordinator:
  enabled: true
  providerRoute: __PROVIDER_ROUTE__
  harness: codex
  workspace: epic-coordinator-home
  threadPolicy: persistent
  instructions: |-
    You supervise epics end to end. Inspect the repositories already present in your workspace before asking for information.
    Keep repositories on the base branches described by your skills and instructions. Do not edit code or push commits.
    Turn product discussion into explicit decisions, an independently verified prototype, and implementation-ready tasks.
    Decompose large work among focused agents with non-overlapping ownership. Use a separate agent to review every Figma frame and state against screenshots of the exact published artifact.
    Treat audits, worker handoffs, green builds, candidate publication, and failed reviews as intermediate results. Continue correcting and reviewing until PASS or a genuine external blocker needs a person.
  skills: []
  prototype:
    mode: interactive-bundle
    instructions: Import and compile the product frontend's actual shell, components, styles, fonts and icons; mock only service calls. Build a frame/state coverage manifest, render every scenario, and iterate from independent source-versus-render evidence. Never redraw an approximate standalone replica after merely reading the source.
  network:
    allow: []`;

/** Configures the first-class Agent responsible for Epic planning. */
export function EpicCoordinatorSettingsScreen(): ReactNode {
  const store = useStore();
  const versions = useTeamVersions();
  const active = versions.data?.find((version) => version.state === 'ACTIVE') ?? versions.data?.[0] ?? null;
  const document = useTeamVersionDocument(store.currentTeamId, active?.version ?? null);
  const apply = useApplyTeamDocument();
  const [source, setSource] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [failure, setFailure] = useState<readonly string[]>([]);

  useEffect(() => {
    if (document.data === undefined || document.data === null) return;
    setSource(readEpicCoordinatorSource(parseTeamDocument(document.data)));
    setState('idle');
    setFailure([]);
  }, [active?.version, document.data]);

  if (store.currentTeamId === null) return <p className="placeholder-inline">No team is active.</p>;
  if (document.data === null) return <p className="placeholder-inline">The active version has no editable team document.</p>;
  if (document.isPending || versions.isPending || source === null) {
    return <QueryPhase isPending error={document.error ?? versions.error} source="Epic Coordinator configuration" rows={5} />;
  }

  const save = (): void => {
    setState('saving');
    setFailure([]);
    void apply(
      (current) => {
        const edited = writeEpicCoordinatorSource(parseTeamDocument(current), source);
        return serializeTeamDocument(ensureEpicCoordinatorWorkspace(edited));
      },
      active?.version,
    ).then(() => setState('saved')).catch((error: unknown) => {
      setState('idle');
      setFailure(describeSaveFailure(error).lines);
    });
  };
  const starter = (): string => {
    const team = readTeam(parseTeamDocument(document.data ?? ''));
    const route = team.providerRoutes.includes('operator-balanced')
      ? 'operator-balanced'
      : team.providerRoutes[0] ?? 'operator-balanced';
    return STARTER.replace('__PROVIDER_ROUTE__', route);
  };

  return <div className="stack">
    <Section
      label="Epic Coordinator"
      lede="A dedicated Agent with one persistent workspace and a separate persistent thread for every epic. Its repositories, branch rules, skills and working conventions are defined here; the platform does not reset or rewrite its workspace."
    >
      <div className="stack compact-stack">
        <p className="hint">This is the same versioned Agent configuration used by the rest of the team. API keys and login secrets stay under Tokens &amp; access and AI providers.</p>
        {source.trim() === '' ? <Flash variant="warning">This team still uses the Main Operator for Epic planning. Create the dedicated coordinator to move Epic chats without deleting their history.</Flash> : null}
        <textarea
          className="stage-yaml-editor"
          aria-label="Epic Coordinator configuration"
          spellCheck={false}
          rows={Math.min(34, Math.max(16, source.split('\n').length + 2))}
          value={source}
          placeholder={STARTER.replace('__PROVIDER_ROUTE__', 'operator-balanced')}
          onChange={(event) => { setSource(event.target.value); setState('idle'); setFailure([]); }}
        />
        <div className="button-row">
          {source.trim() === '' ? <Button onClick={() => setSource(starter())}>Create coordinator</Button> : null}
          <Button variant="primary" disabled={state === 'saving'} onClick={save}>{state === 'saving' ? 'Saving…' : 'Save and apply'}</Button>
        </div>
        {state === 'saved' ? <Flash variant="success">Epic Coordinator is live. Existing Epic conversations are preserved.</Flash> : null}
        {failure.length === 0 ? null : <Flash variant="danger"><strong>The configuration was not applied.</strong><ul>{failure.map((line) => <li key={line}>{line}</li>)}</ul></Flash>}
      </div>
    </Section>
  </div>;
}
